/**
 * chatMessageIdentity.ts — #1882. Everything about a chat message's DATABASE
 * IDENTITY, in one pure, testable place.
 *
 * WHY THIS FILE EXISTS AT ALL. The work is four lines of Index.tsx, and four
 * lines of Index.tsx cannot be unit-tested — Index is a 9,000-line page
 * component that no test mounts. The only guard available there is a regex over
 * its source, and this repo has been burned repeatedly by guards that match
 * prose, match a duplicate, or go vacuous on a rename. Pulling the SELECT list
 * and the row→Message mapping out here makes the thing the guard is protecting
 * into something a real test can execute, and leaves the source scan with the
 * much smaller job of proving Index still calls it.
 *
 * ⚠️ THE TRAP THIS CLOSES. The history read used to be
 * `.select("role,content,created_at")` mapped through `{ role, content }` — an
 * explicit select list feeding an explicit object literal. A column added to
 * ONE of them and not the other is dropped in total silence: no error, no type
 * failure, just a field that is always undefined. Both halves now live here,
 * next to each other, and `chatMessageIdentity.test.ts` fails if either drops
 * the id.
 */

import type { Message } from "@/components/ChatMessages";

/**
 * The columns `handleSelectConversation` reads back from `messages`.
 *
 * ⚠️ A STRING CONSTANT, not an inline literal at the call site, so the source
 * guard can prove Index.tsx reads exactly these columns without re-typing them
 * (a re-typed literal is free to drift from the mapper below, which is the
 * whole defect). `created_at` is selected but deliberately NOT mapped — it is
 * the `.order()` key, and PostgREST will not order by a column it was not asked
 * for. See `mapHistoryRowsToMessages`.
 */
export const CHAT_HISTORY_MESSAGE_COLUMNS = "id,role,content,created_at,saved_list_id";

/**
 * The same list WITHOUT #1877's `saved_list_id`.
 *
 * ⚠️ NOT a tidier alternative — it is the pre-migration fallback, and leaving it
 * out would take her entire chat history down. Code deploys on merge and the
 * SQL is run by hand afterwards, so there is a window in which
 * `messages.saved_list_id` does not exist. PostgREST answers a select naming an
 * absent column with **42703, and it fails the WHOLE read** — so without this
 * retry every conversation she opens in that window renders empty, for a
 * feature she has not been offered yet. See `readConversationMessages`.
 */
export const CHAT_HISTORY_MESSAGE_COLUMNS_BASE = "id,role,content,created_at";

/** One row as the history read returns it. */
export type ChatHistoryMessageRow = {
  id?: string | null;
  role: string;
  content: string;
  created_at?: string | null;
  /** #1877 — the todo_lists row this message's save created, if any. */
  saved_list_id?: string | null;
};

/**
 * Map history rows onto the `Message` shape the chat renders.
 *
 * `id` is carried through so a reloaded message can be addressed the same way
 * a live one can. A row with no id (there should be none — `messages.id` is the
 * primary key — but the select could be narrowed by a future edit) maps to a
 * message with no id rather than to `undefined` in a required field.
 */
export function mapHistoryRowsToMessages(
  rows: ChatHistoryMessageRow[] | null | undefined,
): Message[] {
  return (rows ?? []).map((row) => ({
    ...(row.id ? { id: row.id } : {}),
    role: row.role as "user" | "assistant",
    content: row.content,
    // #1877 — carried only when present. A row read through the pre-migration
    // fallback has no such key at all, and `savedListId: undefined` would be
    // indistinguishable from "this message saved nothing" to a reader that
    // checks `"savedListId" in msg`.
    ...(row.saved_list_id ? { savedListId: row.saved_list_id } : {}),
  }));
}

/** The shape of the one query this module runs. Structural, so a test can fake it. */
type MessagesReader = {
  from: (table: string) => {
    select: (columns: string) => {
      eq: (
        column: string,
        value: string,
      ) => {
        order: (
          column: string,
          opts: { ascending: boolean },
        ) => PromiseLike<{ data: ChatHistoryMessageRow[] | null; error: { code?: string } | null }>;
      };
    };
  };
};

/** PostgREST's "column does not exist". */
const UNDEFINED_COLUMN = "42703";

/**
 * Read one conversation's messages, newest column list first.
 *
 * ⚠️ THE NARROW RETRY FIRES ON 42703 AND NOTHING ELSE. Retrying on any error
 * would silently drop the durable link for a transport blip or an RLS refusal —
 * the feature would look broken with no way to tell why, and the narrow read
 * would then succeed and hide the real failure. A genuine error is rethrown so
 * the caller's existing handler still sees it.
 *
 * Returns messages in `created_at` order; the caller owns the try/catch.
 */
export async function readConversationMessages(
  client: MessagesReader,
  conversationId: string,
): Promise<Message[]> {
  const read = (columns: string) =>
    client
      .from("messages")
      .select(columns)
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });

  let { data, error } = await read(CHAT_HISTORY_MESSAGE_COLUMNS);
  if (error?.code === UNDEFINED_COLUMN) {
    console.warn(
      "[chat-history] messages.saved_list_id is not in the schema yet; reading without it (#1877)",
    );
    ({ data, error } = await read(CHAT_HISTORY_MESSAGE_COLUMNS_BASE));
  }
  if (error) throw error;
  return mapHistoryRowsToMessages(data);
}

/**
 * Attach a freshly minted id to the message at the END of the thread, and only
 * if that message is an assistant message that does not already have one.
 *
 * ⚠️ CALL THIS SYNCHRONOUSLY, at the moment the stream finishes. That instant is
 * the only one where "the trailing assistant message" unambiguously means the
 * reply that just streamed: `RECEIVE_DONE` has already cleared `isLoading`, so
 * she can send again, and a send appends a user message and a fresh empty
 * assistant bubble. Awaiting the insert first and attaching afterwards would
 * have put the id on whichever message happened to be last a round-trip later.
 *
 * The "does not already have one" check is not defensive padding — it is what
 * makes a second call a no-op instead of a silent re-label.
 */
export function attachIdToTrailingAssistant(messages: Message[], id: string): Message[] {
  const last = messages[messages.length - 1];
  if (!last || last.role !== "assistant" || last.id) return messages;
  return messages.map((m, i) => (i === messages.length - 1 ? { ...m, id } : m));
}

/**
 * Remove an id from whichever message carries it.
 *
 * House rule 16, in its awkward form. The id is attached before the write is
 * proved (see above for why it cannot wait), so when the read-back does NOT
 * come back with a row, the id has to be taken off again — otherwise the thread
 * holds a pointer to a `messages` row that does not exist, and the first thing
 * built on top of it (#1877's saved_list_id write-back) would address a missing
 * row and report a failure she cannot act on.
 *
 * Keyed by the id itself, NOT by position: by the time a failure is known, the
 * message may no longer be last.
 */
export function clearMessageId(messages: Message[], id: string): Message[] {
  if (!messages.some((m) => m.id === id)) return messages;
  return messages.map((m) => {
    if (m.id !== id) return m;
    const { id: _dropped, ...rest } = m;
    return rest;
  });
}
